// 用電 › 總覽（plan SPEC NRG-V2；設計稿 v01 / v01b / v01c / v01d / v01e / v01f / v01g）。由上到下：
//   ① 需要處理（只列不是 0 的；每列一個動作；嚴重度 → 已開事故 → 數值）
//   ② 三張卡：本月到今天用電（比較照 8.0 三條件）、估算電費（逐幣別一行，最多 3 行）、碳排放（組織可隱藏、標係數年度）
//   ③ 趨勢：〔日〕〔月〕〔年〕；只有一個{leaf}時可切「依種類」；多個{leaf}時〔比較{leaf}〕最多 5 個疊折線
//   ④ 依{leaf}表（預設依「比上月」由多到少；不符比較條件寫「—」可聚焦看原因）；範圍跨 2 個以上組織時改「依組織」
//   ⑤ 有 kmgr.read、範圍內有 UPS：不斷電系統一行（UPS 輸出不加進上面的總數）
// 狀態（UX-04）：殼層先畫、卡片骨架、不先畫 0；全部讀不到 → 錯誤三段、不畫卡片；部分讀不到 → 卡片小字「N 個{leaf}讀不到，未計入」。
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';
import { useApp } from '../../lib/AppContext';
import { useScope } from '../../lib/scope';
import { useViewportWidth } from '../../lib/useViewport';
import { Dialog } from '../../components/Dialog';
import { brandName } from '../kmgr/parts';
import { useKmgrNav } from '../kmgr/hooks';
import { powerApi, mergePowerSummary, type PowerSummary } from '../kmgr/power';
import { energyApi, energyMode } from './api';
import { BarChart, Legend, LineChart, type BarCell } from './Chart';
import { hhmm, localParts, overThreshold, pctFloor, utcOffsetLabel, type Fmt } from './format';
import { useFmt, useScopeSummary } from './hooks';
import { compareKey, costLines } from './model';
import { Cmp, LoadError, UnitHint } from './parts';
import type { Attention, EnergySummary, HomeRow, OrgRow, Period, SeriesPoint } from './types';

type T = (k: string, o?: Record<string, unknown>) => string;

export function Overview() {
  const { t } = useTranslation();
  const fmt = useFmt();
  const month = useScopeSummary('month');
  const [trend, setTrend] = useState<Period>('month');
  const trendQ = useScopeSummary(trend === 'month' ? 'month' : trend);
  const d = month.data;
  if (month.loading && !d) return <OverviewSkeleton />;
  if (month.error || (!d && !month.loading && month.targets.length)) return <LoadError error={month.error} retry={month.refetch} testId="en-load-error" />;
  if (!d || d.homes_with_meters === 0) return <EmptyState />;
  return (
    <div data-testid="en-overview">
      {month.partialError && (
        <div className="km-bar warn" role="status" data-testid="en-partial">
          <span className="ic" aria-hidden>
            !
          </span>
          <span className="txt small">{t('energy:partial_err')}</span>
        </div>
      )}
      <NeedsAttention d={d} fmt={fmt} />
      <Cards d={d} fmt={fmt} />
      {d.poe_meters > 0 && <p className="en-note" data-testid="en-poe-note">{t('energy:card.poe_note')}</p>}
      <Trend d={trendQ.data ?? d} period={trend} setPeriod={setTrend} fmt={fmt} loading={trendQ.loading} />
      {d.by_org && d.by_org.length > 1 ? <ByOrg rows={d.by_org} d={d} fmt={fmt} /> : <ByHome rows={d.by_home} d={d} fmt={fmt} />}
      <UpsBlock />
    </div>
  );
}

function OverviewSkeleton() {
  const { t } = useTranslation();
  return (
    <div aria-busy="true" aria-label={t('energy:loading')} data-testid="en-skeleton">
      <section className="card en-section">
        <div className="skel" style={{ width: '30%' }} />
        <div className="skel" style={{ width: '70%' }} />
      </section>
      <div className="en-cards">
        {[0, 1, 2].map((i) => (
          <section key={i} className="card">
            <div className="skel" style={{ width: '50%' }} />
            <div className="skel" style={{ width: '80%', height: 24 }} />
            <div className="skel" style={{ width: '60%' }} />
          </section>
        ))}
      </div>
      <section className="card">
        <div className="skel" style={{ height: 150 }} />
      </section>
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  const { config } = useApp();
  const { scopeLabel, setScope, scope } = useScope();
  return (
    <section className="card en-section" data-testid="en-empty">
      <p>{t('energy:empty', { scope: scopeLabel, brand: brandName(config.display_name) })}</p>
      {scope !== 'all' && (
        <button type="button" className="btn" onClick={() => setScope('all')}>
          {t('energy:empty_all')}
        </button>
      )}
    </section>
  );
}

// ---- ① 需要處理 ----

function homeName(d: EnergySummary, id: string): string {
  return d.names?.homes?.[id]?.name ?? id;
}

function NeedsAttention({ d, fmt }: { d: EnergySummary; fmt: Fmt }) {
  const { t } = useTranslation();
  const { setScope } = useScope();
  const nav = useNavigate();
  const [all, setAll] = useState(false);
  const items = d.attention;
  if (!items.length) return null;
  const MAX = 4;
  const shown = all ? items : items.slice(0, MAX);
  const orgPrefix = (a: Attention) => (d.by_org && d.by_org.length > 1 && a.org_id ? (d.names?.orgs?.[a.org_id] ?? a.org_id) + ' › ' : '');
  const row = (a: Attention, i: number) => {
    const home = orgPrefix(a) + homeName(d, a.home_id);
    const dev = a.device_ref ? (d.names?.devices?.[a.device_ref]?.name ?? a.device_ref) : '';
    let ic = '!';
    let cls = 'attention';
    let title = '';
    let detail = '';
    let act: { text: string; go: () => void } | null = null;
    const devPath = a.device_ref ? `/devices/${encodeURIComponent(a.home_id)}/${encodeURIComponent(a.device_ref)}` : '';
    switch (a.kind) {
      case 'plug_overload':
        ic = '✕';
        cls = 'bad';
        title = t('energy:need.plug_title', { home, name: dev });
        detail = t(a.source === 'user' ? 'energy:need.plug_user' : 'energy:need.plug_rated', { time: a.at ? hhmm(a.at, d.names?.homes?.[a.home_id]?.tz) : '', w: fmt.power(a.power_mw), limit: fmt.power(a.limit_mw) });
        act = { text: t('energy:need.act_device'), go: () => nav(devPath + '?tab=overview#energy') };
        break;
      case 'poe_budget': {
        const pct = a.allocated_w && a.rated_w ? pctFloor(a.allocated_w, a.rated_w) : 0;
        // 門檻用原始值判斷（誠實顯示）：平台說 high 但原始值沒到就降成注意
        const high = a.level === 'high' && (!a.allocated_w || !a.rated_w || overThreshold(a.allocated_w, a.rated_w, a.threshold_pct ?? 95));
        title = t(high ? 'energy:need.poe_title_high' : 'energy:need.poe_title_warn', { home, name: dev || 'Station', pct: a.threshold_pct ?? (high ? 95 : 80) });
        detail = [
          t('energy:need.poe_detail', { alloc: fmt.watts(a.allocated_w, 1), rated: fmt.watts(a.rated_w), pct, used: fmt.watts(a.used_w) }),
          a.incident_id ? t('energy:need.poe_incident') : t('energy:need.poe_more_cam'),
        ].join(' · ');
        act = a.incident_id
          ? { text: t('energy:need.act_incident'), go: () => nav(`/kmgr/incidents/${encodeURIComponent(a.home_id)}/${encodeURIComponent(a.incident_id!)}`) }
          : { text: t('energy:need.act_poe'), go: () => nav(devPath + '?tab=channels#site') };
        break;
      }
      case 'home_unavailable': {
        ic = '⊘';
        cls = 'unk';
        const h = a.since ? Math.max(1, Math.round((Date.now() - Date.parse(a.since)) / 3600_000)) : 0;
        title = t('energy:need.home_unavailable', { home, h, since: a.since ? hhmm(a.since, d.names?.homes?.[a.home_id]?.tz) : '' });
        detail = t('energy:need.home_unavailable_d');
        act = { text: t('energy:need.act_home'), go: () => setScope('home:' + a.home_id) };
        break;
      }
      case 'meter_gap': {
        ic = '⊘';
        cls = 'unk';
        const h = a.since ? Math.max(1, Math.floor((Date.now() - Date.parse(a.since)) / 3600_000)) : 2;
        title = t('energy:need.gap_title', { n: a.meters ?? 0, h });
        detail = t('energy:need.gap_detail', { list: (a.by_home ?? []).map((x) => t('energy:need.gap_item', { home: homeName(d, x.home_id), n: x.meters })).join('、') });
        act = { text: t('energy:need.act_which'), go: () => nav('/energy/ranking?gaps=1') };
        break;
      }
    }
    return (
      <li key={i} data-kind={a.kind} data-level={a.level}>
        <span className={`ic ${cls}`} aria-hidden>
          {ic}
        </span>
        <div>
          <div className="t">{title}</div>
          <div className="d">{detail}</div>
        </div>
        {act && (
          <button type="button" className="btn btn-sm" onClick={act.go} aria-label={`${act.text}：${title}`}>
            {act.text}
          </button>
        )}
      </li>
    );
  };
  return (
    <section className="card en-section" data-testid="en-need" aria-labelledby="en-need-h">
      <div className="en-row">
        <h2 id="en-need-h" className="section-title" style={{ margin: 0 }}>
          {t('energy:need.title')}
        </h2>
        <span className="muted small">{items.length > MAX && !all ? t('energy:need.count_shown', { n: items.length, shown: MAX }) : t('energy:need.count', { n: items.length })}</span>
      </div>
      <ul className="en-need">{shown.map(row)}</ul>
      {items.length > MAX && !all && (
        <button type="button" className="btn btn-sm" onClick={() => setAll(true)}>
          {t('energy:need.show_all', { n: items.length })}
        </button>
      )}
    </section>
  );
}

// ---- ② 三張卡 ----

function Cards({ d, fmt }: { d: EnergySummary; fmt: Fmt }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { visible } = useScope();
  const c = d.comparison;
  const excluded = c?.excluded ?? [];
  const exText = excluded
    .slice(0, 4)
    .map((x) => t(x.reason === 'meters_changed' ? 'energy:card.cmp_ex_new' : x.reason === 'prev_coverage' ? 'energy:card.cmp_ex_cov' : 'energy:card.cmp_ex_other', { home: homeName(d, x.home_id) }))
    .join('、');
  const canSetTariff = visible.some((h) => h.role === 'owner' || h.role === 'admin');
  const costs = costLines(d.cost_by_currency);
  const showCarbon = d.show_carbon !== false && d.carbon.length > 0;
  return (
    <div className="en-cards" style={showCarbon ? undefined : { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
      <section className="card" data-testid="en-card-energy">
        <h2>{t('energy:card.energy')}</h2>
        <div className="en-big">
          {fmt.energyNum(d.mwh)}
          <span className="u">{fmt.unitText}</span>
          <UnitHint fmt={fmt} />
        </div>
        {c ? (
          <>
            <div className="en-cmp" data-testid="en-card-cmp">
              {t('energy:card.cmp_prev_month')} <Cmp fmt={fmt} cmp={c} cur={c.cur_mwh} />
            </div>
            <div className="en-note">
              {t('energy:card.cmp_basis', { homes: c.homes, meters: c.meters })}
              {exText && <>；{t('energy:card.cmp_excluded', { list: exText })}</>}
            </div>
          </>
        ) : (
          <div className="en-note">{t('energy:card.cmp_none', { why: t(`energy:cmp.why_${d.no_compare_reason === 'prev_coverage' ? 'prev_coverage' : 'no_prev'}`, { pct: 0 }) })}</div>
        )}
        {d.incomplete_meters > 0 && (
          <div className="en-note">
            <Link to="/energy/ranking?gaps=1" data-testid="en-incomplete">
              {t('energy:card.incomplete', { meters: d.incomplete_meters, hours: d.incomplete_hours })} ›
            </Link>
          </div>
        )}
        {d.unavailable_homes.length > 0 && <div className="en-note">{t('energy:card.unavailable_homes', { n: d.unavailable_homes.length })}</div>}
        {d.forecast_mwh ? <div className="en-note">{t('energy:card.forecast', { v: fmt.approxEnergy(d.forecast_mwh) })}</div> : null}
        {d.visible_homes < d.total_homes && <div className="en-note">{t('energy:card.visible_only', { visible: d.visible_homes, total: d.total_homes })}</div>}
      </section>
      <section className="card" data-testid="en-card-cost">
        <h2>{t('energy:card.cost')}</h2>
        {d.cost_by_currency.length === 0 ? (
          <>
            <p className="muted">{t('energy:card.no_tariff_any')}</p>
            {canSetTariff && (
              <button type="button" className="btn btn-sm" onClick={() => nav('/org/energy')}>
                {t('energy:card.set_tariff')}
              </button>
            )}
          </>
        ) : (
          <ul className="lines">
            {costs.shown.map((m) => (
              <li key={m.currency}>
                {fmt.money(m)}
                {d.cost_by_currency.length > 1 && <span className="sub">{t('energy:card.cost_homes', { n: m.homes })}</span>}
              </li>
            ))}
            {costs.more > 0 && (
              <li className="more">
                <Link to="/energy/report">{t('energy:card.cost_more', { n: costs.more })} ›</Link>
              </li>
            )}
          </ul>
        )}
        {d.cost_by_currency.length > 1 && <div className="en-note">{t('energy:card.cost_note')}</div>}
        {d.homes_without_tariff.length > 0 && d.cost_by_currency.length > 0 && <div className="en-note">{t('energy:card.no_tariff', { n: d.homes_without_tariff.length })}</div>}
        <div className="en-note">{t('energy:card.cost_disclaimer')}</div>
      </section>
      {showCarbon && (
        <section className="card" data-testid="en-card-carbon">
          <h2>{t('energy:card.carbon')}</h2>
          <ul className="lines">
            {d.carbon.map((c) => (
              <li key={c.region + c.gas}>
                {fmt.carbon(c.kg, c.gas)}
                {d.carbon.length > 1 && <span className="sub">{c.region}</span>}
              </li>
            ))}
          </ul>
          <div className="en-note">
            {d.carbon
              .map(
                (c) =>
                  t('energy:card.carbon_note', { region: c.region, provisional: c.provisional ? t('energy:card.carbon_provisional') : '', year: c.factor_year, source: c.source ?? '' }) +
                  (c.gas === 'co2' ? t('energy:card.carbon_co2only') : ''),
              )
              .join(' · ')}{' '}
            · {t('energy:card.carbon_ref')}
          </div>
        </section>
      )}
    </div>
  );
}

// ---- ③ 趨勢 ----

function cellLabel(t: T, p: SeriesPoint, period: Period, tz?: string): string {
  const lp = localParts(p.t, tz);
  if (period === 'day') return t('energy:chart.hour_label', { h: lp.h });
  if (period === 'year') return t('energy:chart.month_label', { m: lp.m });
  return t('energy:chart.day_label', { m: lp.m, d: lp.d });
}

export function seriesCells(t: T, fmt: Fmt, pts: SeriesPoint[], period: Period, tz?: string, valueOf: (p: SeriesPoint) => number | null = (p) => p.mwh): BarCell[] {
  const now = Date.now();
  return pts.map((p) => {
    const label = cellLabel(t, p, period, tz);
    const future = Date.parse(p.t) > now;
    const v = valueOf(p);
    const q = p.quality === 'measured' ? t('energy:q.measured') : p.quality === 'estimated' ? t('energy:q.estimated') : t('energy:q.incomplete');
    const shortLabel = period === 'day' ? String(localParts(p.t, tz).h) : period === 'year' ? label : String(localParts(p.t, tz).d);
    return {
      value: future ? null : v === null ? null : v / 1e6,
      quality: future ? undefined : v === null ? 'incomplete' : p.quality,
      live: !!p.partial && !future,
      label: shortLabel,
      say: future
        ? ''
        : v === null
          ? t('energy:chart.say_miss', { label })
          : p.partial
            ? t('energy:chart.say_live', { label, v: fmt.energy(v), time: hhmm(now, tz) })
            : t('energy:chart.say_day', { label, v: fmt.energy(v), q }),
    };
  });
}

function Trend({ d, period, setPeriod, fmt, loading }: { d: EnergySummary; period: Period; setPeriod: (p: Period) => void; fmt: Fmt; loading: boolean }) {
  const { t } = useTranslation();
  const { visible } = useScope();
  const isMobile = useViewportWidth() <= 800;
  const [byKind, setByKind] = useState(false);
  const [cmpHomes, setCmpHomes] = useState<string[]>([]);
  const [picking, setPicking] = useState(false);
  const single = d.by_home.length === 1;
  const tz = d.tz;
  const title = t(period === 'day' ? 'energy:trend.title_day' : period === 'year' ? 'energy:trend.title_year' : 'energy:trend.title_month');
  let pts = d.series;
  // 手機：只畫最近 7 格（日 / 月），其餘用〔‹ 前一天〕〔下一天 ›〕
  const [page, setPage] = useState(0);
  if (isMobile && period === 'month') {
    const lastIdx = pts.reduce((a, p, i) => (p.mwh !== null || p.partial ? i : a), 0);
    const end = Math.max(7, lastIdx + 1 - page * 7);
    pts = pts.slice(Math.max(0, end - 7), end);
  }
  const cells = seriesCells(t, fmt, pts, period, tz);
  const table = {
    head: [t('energy:chart.col_time'), fmt.unitText, t('energy:chart.col_quality')],
    rows: pts.filter((p) => Date.parse(p.t) <= Date.now()).map((p) => [cellLabel(t, p, period, tz), p.mwh === null ? '—' : fmt.energyNum(p.mwh), p.mwh === null ? t('energy:q.incomplete') : t(`energy:q.${p.quality}`)]),
    total: [t('energy:chart.total'), fmt.energyNum(pts.reduce((a, p) => a + (p.mwh ?? 0), 0)), ''],
  };
  return (
    <section className="card en-section" data-testid="en-trend" aria-busy={loading}>
      <div className="en-row">
        <h2 className="section-title" style={{ margin: 0 }}>
          {cmpHomes.length ? t('energy:trend.compare_n', { n: cmpHomes.length }) : title}
        </h2>
        <span className="spacer" />
        <div className="km-seg" role="group" aria-label={t('energy:trend.granularity')}>
          {(['day', 'month', 'year'] as Period[]).map((p) => (
            <button key={p} type="button" aria-pressed={period === p} onClick={() => (setPeriod(p), setPage(0))}>
              {t(`energy:trend.${p}`)}
            </button>
          ))}
        </div>
        {single && (
          <div className="km-seg" role="group" aria-label={t('energy:trend.by_kind')}>
            <button type="button" aria-pressed={!byKind} onClick={() => setByKind(false)}>
              {t('energy:trend.total_only')}
            </button>
            <button type="button" aria-pressed={byKind} onClick={() => setByKind(true)} data-testid="en-bykind">
              {t('energy:trend.by_kind')}
            </button>
          </div>
        )}
        {!single && visible.length > 1 && (
          <button type="button" className="btn btn-sm" onClick={() => setPicking(true)} data-testid="en-compare-btn">
            {cmpHomes.length ? t('energy:trend.compare_n', { n: cmpHomes.length }) : t('energy:trend.compare')}
          </button>
        )}
      </div>
      {d.mixed_tz && <p className="en-note">{t('energy:chart.tz_note', { tz: utcOffsetLabel(tz) })}</p>}
      {cmpHomes.length ? (
        <CompareHomes homes={cmpHomes} d={d} fmt={fmt} clear={() => setCmpHomes([])} />
      ) : byKind && single ? (
        <KindLines d={d} fmt={fmt} period={period} />
      ) : (
        <>
          <BarChart
            title={title}
            unit={fmt.unitText}
            cells={cells}
            labelEvery={period === 'day' ? 3 : period === 'month' && !isMobile ? 7 : 1}
            table={table}
            testId="en-trend-chart"
            stepLabels={isMobile ? [t('energy:chart.prev_day'), t('energy:chart.next_day')] : undefined}
          after={
            <Legend
              items={[
                { k: 'bar', text: t('energy:trend.daily_total') },
                ...(cells.some((c) => c.quality === 'estimated') ? [{ k: 'est', text: t('energy:chart.legend_est') }] : []),
                ...(cells.some((c) => c.value === null && c.quality === 'incomplete') ? [{ k: 'miss', text: t('energy:chart.legend_miss') }] : []),
                ...(cells.some((c) => c.live) ? [{ k: 'live', text: t('energy:chart.legend_live') }] : []),
              ]}
            />
          }
        />
          {isMobile && period === 'month' && (
            <div className="en-stepper">
              <button type="button" className="btn btn-sm" onClick={() => setPage((p) => p + 1)} aria-disabled={d.series.length - (page + 1) * 7 <= 0}>
                <span aria-hidden>‹</span> {t('energy:chart.prev_cell')} 7
              </button>
              <button type="button" className="btn btn-sm" onClick={() => setPage((p) => Math.max(0, p - 1))} aria-disabled={page === 0}>
                {t('energy:chart.next_cell')} 7 <span aria-hidden>›</span>
              </button>
            </div>
          )}
        </>
      )}
      {picking && (
        <ComparePicker
          d={d}
          initial={cmpHomes}
          close={() => setPicking(false)}
          done={(ids) => {
            setCmpHomes(ids);
            setPicking(false);
          }}
        />
      )}
    </section>
  );
}

function KindLines({ d, fmt, period }: { d: EnergySummary; fmt: Fmt; period: Period }) {
  const { t } = useTranslation();
  const kinds = (['plug', 'poe', 'meter'] as const).filter((k) => d.series.some((p) => (p.by_kind?.[k] ?? 0) > 0));
  const pts = d.series.filter((p) => Date.parse(p.t) <= Date.now());
  const labels = pts.map((p) => cellLabel(t, p, period, d.tz));
  return (
    <LineChart
      title={t('energy:trend.by_kind')}
      unit={fmt.unitText}
      labels={labels.map((l, i) => (i % Math.ceil(labels.length / 6) === 0 ? l : ''))}
      series={kinds.map((k, i) => ({ name: t(`energy:kind.${k}`), color: i + 1, values: pts.map((p) => (p.mwh === null ? null : (p.by_kind?.[k] ?? 0) / 1e6)) }))}
      partialLast
      table={{
        head: [t('energy:chart.col_time'), ...kinds.map((k) => t(`energy:kind.${k}`))],
        rows: pts.map((p, i) => [labels[i], ...kinds.map((k) => (p.mwh === null ? '—' : fmt.energyNum(p.by_kind?.[k] ?? 0)))]),
      }}
      testId="en-kind-chart"
    />
  );
}

function ComparePicker({ d, initial, close, done }: { d: EnergySummary; initial: string[]; close: () => void; done: (ids: string[]) => void }) {
  const { t } = useTranslation();
  const [sel, setSel] = useState<string[]>(initial);
  const rows = [...d.by_home].sort((a, b) => b.mwh - a.mwh);
  return (
    <Dialog open title={t('energy:trend.compare_title')} onClose={close} testId="en-compare-dlg">
      <ul className="en-pick" style={{ maxHeight: 360, overflow: 'auto' }}>
        {rows.map((r) => {
          const on = sel.includes(r.home_id);
          const dis = !on && sel.length >= 5;
          return (
            <li key={r.home_id} className={dis ? 'dis' : undefined}>
              <input
                type="checkbox"
                id={`cmp-${r.home_id}`}
                checked={on}
                disabled={dis}
                onChange={() => setSel((s) => (on ? s.filter((x) => x !== r.home_id) : [...s, r.home_id]))}
              />
              <label htmlFor={`cmp-${r.home_id}`}>{homeName(d, r.home_id)}</label>
              <span className="muted small">{d.names?.homes?.[r.home_id]?.path}</span>
            </li>
          );
        })}
      </ul>
      <p className="muted small">{t('energy:trend.compare_max')}</p>
      <div className="dialog-actions">
        {initial.length > 0 && (
          <button type="button" className="btn" onClick={() => done([])}>
            {t('energy:trend.compare_clear')}
          </button>
        )}
        <button type="button" className="btn btn-primary" disabled={!sel.length} onClick={() => done(sel)}>
          {t('energy:trend.compare_done')}
        </button>
      </div>
    </Dialog>
  );
}

function CompareHomes({ homes, d, fmt, clear }: { homes: string[]; d: EnergySummary; fmt: Fmt; clear: () => void }) {
  const { t } = useTranslation();
  const { homes: scopeHomes } = useScope();
  const from = d.from;
  const to = d.to;
  const qs = useQueries({
    queries: homes.map((h) => {
      const api = scopeHomes.find((x) => x.home_id === h)?.endpoints?.api ?? '';
      return { queryKey: ['energy', 'series', energyMode(), h, from, to], queryFn: () => energyApi.homeSeries(api, h, 'day', from, to), staleTime: 60_000 };
    }),
  });
  if (qs.some((q) => q.isLoading)) return <div className="skel" style={{ height: 150 }} />;
  const pts = qs[0]?.data?.points ?? [];
  const now = Date.now();
  const idx = pts.map((p, i) => (Date.parse(p.t) <= now ? i : -1)).filter((i) => i >= 0);
  const labels = idx.map((i) => {
    const lp = localParts(pts[i].t, d.tz);
    return t('energy:chart.day_label', { m: lp.m, d: lp.d });
  });
  const series = homes.map((h, k) => ({ name: homeName(d, h), color: k + 1, values: idx.map((i) => (qs[k].data?.points[i]?.mwh ?? null) === null ? null : qs[k].data!.points[i].mwh! / 1e6) }));
  return (
    <>
      <LineChart
        title={t('energy:trend.compare_n', { n: homes.length })}
        unit={fmt.unitText}
        labels={labels.map((l, i) => (i % 7 === 0 || i === labels.length - 1 ? l : ''))}
        series={series}
        partialLast
        table={{ head: [t('energy:chart.col_time'), ...series.map((s) => s.name)], rows: labels.map((l, i) => [l, ...series.map((s) => (s.values[i] === null ? '—' : fmt.energyNum(s.values[i]! * 1e6)))]) }}
        testId="en-compare-chart"
      />
      <button type="button" className="btn btn-sm" onClick={clear}>
        {t('energy:trend.compare_clear')}
      </button>
    </>
  );
}

// ---- ④ 依{leaf} / 依組織 ----

function costText(fmt: Fmt, list: { currency: string; amount: number }[]): string {
  return list.map((m) => fmt.money(m)).join(' · ');
}

function ByHome({ rows, d, fmt }: { rows: HomeRow[]; d: EnergySummary; fmt: Fmt }) {
  const { t } = useTranslation();
  const { setScope } = useScope();
  const [sort, setSort] = useState<'cmp' | 'energy'>('cmp');
  const [all, setAll] = useState(false);
  const sorted = useMemo(() => [...rows].sort((a, b) => (sort === 'cmp' ? compareKey(b) - compareKey(a) : b.mwh - a.mwh) || b.mwh - a.mwh), [rows, sort]);
  if (rows.length <= 1) return null;
  const MAX = 10;
  const shown = all ? sorted : sorted.slice(0, MAX);
  const name = (r: HomeRow) => (
    <>
      <button type="button" className="link-btn" onClick={() => setScope('home:' + r.home_id)} style={{ fontWeight: 600, textDecoration: 'underline', color: 'inherit' }}>
        {homeName(d, r.home_id)}
      </button>
      {d.names?.homes?.[r.home_id]?.path && <div className="en-note">{d.names.homes[r.home_id].path}</div>}
    </>
  );
  return (
    <section className="card en-section" data-testid="en-by-home">
      <div className="en-row">
        <h2 className="section-title" style={{ margin: 0 }}>
          {t('energy:table.by_home')}
        </h2>
        <span className="spacer" />
        <div className="km-seg" role="group" aria-label={t('energy:rank.sort')}>
          <button type="button" aria-pressed={sort === 'cmp'} onClick={() => setSort('cmp')}>
            {t('energy:table.sort_cmp')}
          </button>
          <button type="button" aria-pressed={sort === 'energy'} onClick={() => setSort('energy')}>
            {t('energy:table.sort_energy')}
          </button>
        </div>
      </div>
      {sort === 'cmp' && <p className="en-note">{t('energy:table.sort_hint')}</p>}
      <div className="table-wrap en-desk-only">
        <table className="km-table">
          <thead>
            <tr>
              <th scope="col">{t('energy:table.col_home')}</th>
              <th scope="col" className="num">
                {t('energy:table.col_kwh')}
              </th>
              <th scope="col" className="num" aria-sort={sort === 'cmp' ? 'descending' : undefined}>
                {t('energy:table.col_cmp')}
              </th>
              <th scope="col" className="num">
                {t('energy:table.col_daily')}
              </th>
              <th scope="col" className="num">
                {t('energy:table.col_cost')}
              </th>
              <th scope="col" className="num">
                {t('energy:table.col_meters')}
              </th>
              <th scope="col" className="num">
                {t('energy:table.col_unavail')}
              </th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.home_id} data-home={r.home_id} className={r.unavailable ? 'en-miss' : undefined}>
                <td>{name(r)}</td>
                <td className="r tabular">{r.unavailable ? t('energy:table.unavailable') : fmt.energyNum(r.mwh)}</td>
                <td className="r">
                  <Cmp fmt={fmt} cmp={r.comparison} cur={r.mwh} reason={r.no_compare_reason} prevCoverage={r.prev_coverage} />
                </td>
                <td className="r tabular">{r.unavailable ? '—' : fmt.energyNum(r.daily_avg_mwh)}</td>
                <td className="r tabular">{r.no_tariff ? <span className="muted">{t('energy:table.no_tariff')}</span> : costText(fmt, r.costs)}</td>
                <td className="r tabular">{r.meters}</td>
                <td className="r tabular">{r.unavailable_meters || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="en-cardlist en-mobile-only">
        {shown.map((r) => {
          const cmp = r.comparison ? fmt.compare(r.comparison.ratio, r.comparison.prev_mwh, r.mwh).text : '—';
          const cost = r.no_tariff ? t('energy:table.no_tariff') : costText(fmt, r.costs);
          return (
            <li key={r.home_id}>
              <div className="l1">
                {name(r)}
                <span className="tabular">{r.unavailable ? t('energy:table.unavailable') : fmt.energy(r.mwh)}</span>
              </div>
              <div className="l2">{r.unavailable_meters ? t('energy:table.m2', { cmp, cost, n: r.unavailable_meters }) : t('energy:table.m2_ok', { cmp, cost })}</div>
            </li>
          );
        })}
      </ul>
      {rows.length > MAX && !all && (
        <p className="en-note">
          {t('energy:table.more', { n: rows.length - MAX })} ·{' '}
          <button type="button" className="link-btn" onClick={() => setAll(true)}>
            {t('energy:table.show_all', { n: rows.length })}
          </button>
        </p>
      )}
    </section>
  );
}

function ByOrg({ rows, d, fmt }: { rows: OrgRow[]; d: EnergySummary; fmt: Fmt }) {
  const { t } = useTranslation();
  const sorted = [...rows].sort((a, b) => compareKey(b) - compareKey(a) || b.mwh - a.mwh);
  return (
    <section className="card en-section" data-testid="en-by-org">
      <h2 className="section-title">{t('energy:table.by_org')}</h2>
      <div className="table-wrap">
        <table className="km-table">
          <thead>
            <tr>
              <th scope="col">{t('energy:table.col_org')}</th>
              <th scope="col" className="num">
                {t('energy:table.col_kwh')}
              </th>
              <th scope="col" className="num">
                {t('energy:table.col_cmp')}
              </th>
              <th scope="col" className="num">
                {t('energy:table.col_cost')}
              </th>
              <th scope="col" className="num">
                {t('energy:table.col_meters')}
              </th>
              <th scope="col" className="num">
                {t('energy:table.col_unavail')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.org_id} data-org={r.org_id}>
                <td>
                  <b>{d.names?.orgs?.[r.org_id] ?? r.org_id}</b>
                  <div className="en-note">{t('energy:table.homes_count', { n: r.homes_with_meters })}</div>
                </td>
                <td className="r tabular">{fmt.energyNum(r.mwh)}</td>
                <td className="r">
                  <Cmp fmt={fmt} cmp={r.comparison} cur={r.mwh} reason={r.no_compare_reason} prevCoverage={r.prev_coverage} />
                </td>
                <td className="r tabular">
                  {r.costs.map((m) => (
                    <div key={m.currency}>{fmt.money(m)}</div>
                  ))}
                  {r.no_tariff_homes > 0 && <div className="muted small">{t('energy:card.no_tariff', { n: r.no_tariff_homes })}</div>}
                </td>
                <td className="r tabular">{r.meters}</td>
                <td className="r tabular">{r.unavailable_meters || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="en-note">{t('energy:table.cross_currency')}</p>
    </section>
  );
}

// ---- ⑤ 不斷電系統 ----

function UpsBlock() {
  const { t } = useTranslation();
  const kmgr = useKmgrNav();
  const { visible, orgs } = useScope();
  const orgIds = [...new Set(visible.filter((h) => !h.external && h.org_id).map((h) => h.org_id!))];
  const qs = useQueries({
    queries: orgIds.map((o) => {
      const api = orgs.find((x) => x.org_id === o)?.api ?? '';
      return { queryKey: ['kmgr', 'power', o], queryFn: () => powerApi.org(api, o), enabled: kmgr.visible && !!api, retry: false, staleTime: 30_000 };
    }),
  });
  if (!kmgr.visible) return null;
  const ok = qs.filter((q) => q.data).map((q) => q.data!.summary);
  if (!ok.length) return null;
  const s: PowerSummary = mergePowerSummary(ok);
  if (!s.ups_total) return null;
  const min = s.ups_min_runtime_s !== undefined ? t('energy:ups.minutes', { n: Math.floor(s.ups_min_runtime_s / 60) }) : undefined;
  const line = s.ups_on_battery
    ? min
      ? t('energy:ups.line_battery', { n: s.ups_total, b: s.ups_on_battery, rt: min })
      : t('energy:ups.line_battery_unk', { n: s.ups_total, b: s.ups_on_battery })
    : t('energy:ups.line_ok', { n: s.ups_total });
  return (
    <section className="card en-section en-ups" data-testid="en-ups">
      <div className="en-row">
        <h2 className="section-title" style={{ margin: 0 }}>
          {t('energy:ups.title')}
        </h2>
        <span className="spacer" />
        <Link to="/kmgr/network?class=ups">{t('energy:ups.go')} ›</Link>
      </div>
      <p style={{ margin: '6px 0 0' }}>
        {line}
        {s.ups_unreadable > 0 && <> · {t('energy:ups.unreadable', { n: s.ups_unreadable })}</>}
      </p>
      <p className="en-note">{t('energy:ups.note')}</p>
    </section>
  );
}
